/**
 * categories.js — the 12 CPG AI Lab toolkit categories (plus the Prompt Builder, Tools directory and Brand Profile = the 15 dashboard tiles).
 * `slug` drives the route /ai-lab/:slug (toolkit page) and the Prompt Builder grouping.
 * `icon` is a Lucide React icon name (resolved in components via the ICONS map).
 * `accent` is a Tailwind color family used for tile/badge tinting.
 */
export const CATEGORIES = [
  {
    id: "packaging",
    slug: "packaging",
    name: "Packaging AI",
    short: "Packaging",
    tagline: "Concepts, briefs, flavor systems, renders and shelf mockups.",
    icon: "Package",
    accent: "orange",
  },
  {
    id: "branding",
    slug: "branding",
    name: "Branding AI",
    short: "Branding",
    tagline: "Names, logo direction, typography, color and identity systems.",
    icon: "Sparkles",
    accent: "violet",
  },
  {
    id: "photography",
    slug: "photography",
    name: "Photography AI",
    short: "Photography",
    tagline: "Studio shots, lifestyle scenes, ingredient imagery and e-commerce sets.",
    icon: "Camera",
    accent: "sky",
  },
  {
    id: "advertising",
    slug: "advertising",
    name: "Advertising AI",
    short: "Advertising",
    tagline: "Meta ads, hooks, headlines, UGC scripts and campaign concepts.",
    icon: "Megaphone",
    accent: "rose",
  },
  {
    id: "video",
    slug: "video",
    name: "Video AI",
    short: "Video",
    tagline: "Scripts, storyboards, product animation, presenters and launch videos.",
    icon: "Clapperboard",
    accent: "red",
  },
  {
    id: "social",
    slug: "social",
    name: "Social AI",
    short: "Social",
    tagline: "30-day calendars, captions, pillars, reels and influencer briefs.",
    icon: "Share2",
    accent: "pink",
  },
  {
    id: "website",
    slug: "website",
    name: "Website AI",
    short: "Website",
    tagline: "Sitemaps, homepage copy, product pages, FAQs and SEO content.",
    icon: "Globe",
    accent: "teal",
  },
  {
    id: "retail",
    slug: "retail",
    name: "Retail AI",
    short: "Retail & Sales",
    tagline: "Buyer decks, sell sheets, pitches, cold emails and objection handling.",
    icon: "Store",
    accent: "amber",
  },
  {
    id: "investor",
    slug: "investor",
    name: "Investor AI",
    short: "Investor",
    tagline: "Pitch decks, executive summaries, investor emails and meeting prep.",
    icon: "TrendingUp",
    accent: "emerald",
  },
  {
    id: "research",
    slug: "research",
    name: "Research AI",
    short: "Research",
    tagline: "Competitors, market sizing, consumer insights, reviews and trends.",
    icon: "Search",
    accent: "indigo",
  },
  {
    id: "finance",
    slug: "finance",
    name: "Finance AI",
    short: "Finance & COGS",
    tagline: "Margins, pricing models, waterfalls, COGS and scenario planning.",
    icon: "Calculator",
    accent: "lime",
  },
  {
    id: "operations",
    slug: "operations",
    name: "Operations AI",
    short: "Operations",
    tagline: "SOPs, production plans, RFQs, QC checklists and inventory planning.",
    icon: "Settings2",
    accent: "slate",
  },
];

export const CATEGORY_BY_ID = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

/** Tailwind classes per accent. Listed explicitly so the JIT compiler keeps them.
 *  Each `badge` string ends with explicit `hover:bg-*` + `hover:text-*` tokens
 *  that keep the category pill's own color family on hover. Without these
 *  the shadcn `Badge secondary` variant defaults to `hover:bg-secondary/80`
 *  which slams a dark grey behind the label and drops WCAG contrast on
 *  every prompt card (Packaging / Branding / Advertising / etc.). */
export const ACCENT_CLASSES = {
  orange:  { tile: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/40 dark:text-pink-300 dark:border-pink-900",  icon: "bg-pink-500",  badge: "bg-pink-100 text-pink-800 hover:bg-pink-200 hover:text-pink-900 dark:bg-pink-900/50 dark:text-pink-200 dark:hover:bg-pink-800/70 dark:hover:text-pink-100" },
  violet:  { tile: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900",  icon: "bg-violet-500",  badge: "bg-violet-100 text-violet-800 hover:bg-violet-200 hover:text-violet-900 dark:bg-violet-900/50 dark:text-violet-200 dark:hover:bg-violet-800/70 dark:hover:text-violet-100" },
  sky:     { tile: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900",                    icon: "bg-sky-500",     badge: "bg-sky-100 text-sky-800 hover:bg-sky-200 hover:text-sky-900 dark:bg-sky-900/50 dark:text-sky-200 dark:hover:bg-sky-800/70 dark:hover:text-sky-100" },
  rose:    { tile: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900",              icon: "bg-rose-500",    badge: "bg-rose-100 text-rose-800 hover:bg-rose-200 hover:text-rose-900 dark:bg-rose-900/50 dark:text-rose-200 dark:hover:bg-rose-800/70 dark:hover:text-rose-100" },
  red:     { tile: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",                    icon: "bg-red-500",     badge: "bg-red-100 text-red-800 hover:bg-red-200 hover:text-red-900 dark:bg-red-900/50 dark:text-red-200 dark:hover:bg-red-800/70 dark:hover:text-red-100" },
  pink:    { tile: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/40 dark:text-pink-300 dark:border-pink-900",              icon: "bg-pink-500",    badge: "bg-pink-100 text-pink-800 hover:bg-pink-200 hover:text-pink-900 dark:bg-pink-900/50 dark:text-pink-200 dark:hover:bg-pink-800/70 dark:hover:text-pink-100" },
  teal:    { tile: "bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-900",              icon: "bg-teal-500",    badge: "bg-teal-100 text-teal-800 hover:bg-teal-200 hover:text-teal-900 dark:bg-teal-900/50 dark:text-teal-200 dark:hover:bg-teal-800/70 dark:hover:text-teal-100" },
  amber:   { tile: "bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/40 dark:text-pink-300 dark:border-pink-900",        icon: "bg-pink-500",   badge: "bg-pink-100 text-pink-800 hover:bg-pink-200 hover:text-pink-900 dark:bg-pink-900/50 dark:text-pink-200 dark:hover:bg-pink-800/70 dark:hover:text-pink-100" },
  emerald: { tile: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900", icon: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 hover:text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200 dark:hover:bg-emerald-800/70 dark:hover:text-emerald-100" },
  indigo:  { tile: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-900",  icon: "bg-indigo-500",  badge: "bg-indigo-100 text-indigo-800 hover:bg-indigo-200 hover:text-indigo-900 dark:bg-indigo-900/50 dark:text-indigo-200 dark:hover:bg-indigo-800/70 dark:hover:text-indigo-100" },
  lime:    { tile: "bg-lime-50 text-lime-700 border-lime-200 dark:bg-lime-950/40 dark:text-lime-300 dark:border-lime-900",              icon: "bg-lime-600",    badge: "bg-lime-100 text-lime-800 hover:bg-lime-200 hover:text-lime-900 dark:bg-lime-900/50 dark:text-lime-200 dark:hover:bg-lime-800/70 dark:hover:text-lime-100" },
  slate:   { tile: "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/60 dark:text-slate-300 dark:border-slate-800",        icon: "bg-slate-600",   badge: "bg-slate-100 text-slate-800 hover:bg-slate-200 hover:text-slate-900 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-slate-100" },
};
